import { Directive, OnDestroy, OnInit } from '@angular/core';
import { FormControls, TypedAbstractControl, TypedFormGroup, ValueAccessorBase } from 'osn-shared';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { deepEqual } from '../../shared/functions/deepEqual';

@Directive()
export abstract class TrayControlValueAccessorBase<
    T extends object,
    Controls extends { [K in keyof T]: TypedAbstractControl<T[K]> } = FormControls<T>
  >
  extends ValueAccessorBase<T>
  implements OnInit, OnDestroy
{
  protected readonly FORM_DEBOUNCE_TIME_MS = 100;
  protected readonly destroyed$: Subject<void> = new Subject<void>();

  public formControls: Controls;
  public form: TypedFormGroup<T>;

  public ngOnInit(): void {
    this.initForm(this.value);
    this.watchFormChanges();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public writeValue(value: T) {
    super.writeValue(value);
    this.setFormValue(value);
  }

  public setDisabledState(isDisabled: boolean): void {
    this.setDisabled(isDisabled);
  }

  protected setDisabled(isDisabled: boolean) {
    if (isDisabled) {
      this.disableForm();
    } else {
      this.enableForm();
    }
    this.form?.updateValueAndValidity();
  }

  protected setFormValue(value: T): void {
    if (!this.form) {
      return;
    }

    const formValue = value ?? this.getDefaultVm();

    this.form.reset({ ...formValue });

    this.setDisabled(this.form.disabled);
  }

  protected watchFormChanges() {
    this.form.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        filter(() => this.form.enabled),
        debounceTime(this.FORM_DEBOUNCE_TIME_MS),
        distinctUntilChanged((a, b) => deepEqual(a, b))
      )
      .subscribe((value: T) => {
        const shouldEmit = !this.form.enabled || this.form.valid;
        this.value = shouldEmit ? { ...this.mapFormValueToEmittedValue(value) } : undefined;
      });
  }

  protected enableForm() {
    this.form?.enable();
  }

  protected disableForm() {
    this.form?.disable();
  }

  protected abstract initForm(value: T);
  protected abstract getDefaultVm(): T;
  protected abstract mapFormValueToEmittedValue(value: T): T;
}
